/**
 * 파일명 : evl0112m.tsx
 * 설명 : 수상내역관리
 * 수정내역
 * **********************************************
 * Version    수정일자      수정자    수정내용
 * **********************************************
 * 1.0       2026-03-09   김한철    최초작성
 */

/************************************************
 * Import 영역
 ************************************************/
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Row,
  Col,
  Space,
  Button,
  Input,
  Select,
  Table,
  Modal,
  Form,
  message,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  PlusOutlined,
  SearchOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  CaretUpOutlined,
  CaretDownOutlined,
} from "@ant-design/icons";

import * as common from "libs/common";
import * as commonProxy from "libs/CommonProxy";

/* Reducer */
import { useAppSelector, useAppDispatch } from "../../../redux/hooks";
import type {
  IAwardRow,
  SelectOption,
  SortKey,
  AwardCodeOption,
} from "../../../redux/models/evl/evl0112m_model";
import { AWARD_MAX_VALUE } from "../../../redux/models/evl/evl0112m_model";
import { Evl0112mAction } from "../../../redux/slice/evl/evl0112m_slice";

/************************************************
 * 유틸
 ************************************************/
const buildCodeMap = (datas: any[]) => {
  const map: Record<string, string> = {};

  (datas ?? []).forEach((item: any) => {
    const cd =
      item.comCd ??
      item.comDtlCd ??
      item.cd ??
      item.code ??
      item.dtlCd ??
      item.value;

    const nm =
      item.comCdNm ??
      item.comDtlNm ??
      item.cdNm ??
      item.codeName ??
      item.dtlNm ??
      item.label;

    if (cd) map[String(cd)] = String(nm ?? cd);
  });

  return map;
};

const buildCodeOptions = (datas: any[]): SelectOption[] => {
  return (datas ?? [])
    .map((item: any) => {
      const value =
        item.comCd ??
        item.comDtlCd ??
        item.cd ??
        item.code ??
        item.dtlCd ??
        item.value;

      const label =
        item.comCdNm ??
        item.comDtlNm ??
        item.cdNm ??
        item.codeName ??
        item.dtlNm ??
        item.label;

      if (!value) return null;

      return {
        value: String(value),
        label: String(label ?? value),
      };
    })
    .filter(Boolean) as SelectOption[];
};

const INITIAL_FILTERS = {
  awardYear: "all",
  empNm: "",
  deptNm: "all",
  postCd: "all",
  awardCd: "all",
};

/************************************************
 * 컴포넌트
 ************************************************/
const Evl0112m = (props: any) => {
  const dispatch = useAppDispatch();

  const rows = useAppSelector((state) => state.Evl0112m.rows);
  const posClsMap = useAppSelector((state) => state.Evl0112m.posClsMap);
  const posOptions = useAppSelector((state) => state.Evl0112m.posOptions);

  const [yearOptions, setYearOptions] = useState<SelectOption[]>([]);
  const [deptOptions, setDeptOptions] = useState<SelectOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [awardCdOptions, setAwardCdOptions] = useState<AwardCodeOption[]>([]);

  const [pageNo, setPageNo] = useState(1);
  const pageSize = 10;

  const [sortKey, setSortKey] = useState<SortKey>("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<IAwardRow | null>(null);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [editForm] = Form.useForm();

  const onChangeFilter = (payload: Partial<typeof filters>) => {
    setFilters((prev) => ({ ...prev, ...payload }));
  };

  /************************************************
   * 공통코드 / 이름 변환
   ************************************************/
  const setPosClsList = useCallback(() => {
    const option = {
      useYn: "Y",
      callback: (datas: any[]) => {
        dispatch(Evl0112mAction.setPosClsMap(buildCodeMap(datas)));
        dispatch(Evl0112mAction.setPosOptions(buildCodeOptions(datas)));
      },
    };
    common.getComCodeList("011", option);
  }, [dispatch]);

  const posName = useCallback(
    (cd?: string | null) => (cd ? posClsMap[cd] ?? cd : ""),
    [posClsMap]
  );

  const awardCdMap = useMemo(() => {
    const map: Record<string, string> = {};
    awardCdOptions.forEach((item) => {
      map[item.value] = item.label;
    });
    return map;
  }, [awardCdOptions]);

  const awardCdName = useCallback(
    (cd?: string | null) => (cd ? awardCdMap[cd] ?? cd : "-"),
    [awardCdMap]
  );

  /************************************************
   * 공통코드 조회 - 수상코드
   ************************************************/
  const loadAwardCodeList = useCallback(() => {
    const option = {
      useYn: "Y",
      callback: (codeItems: any[]) => {
        if (codeItems && Array.isArray(codeItems)) {
          setAwardCdOptions(
            codeItems.map((item: any) => ({
              value: String(item.cd),
              label: String(item.cdNm),
            }))
          );
        }
      },
    };

    common.getComCodeList("065", option);
  }, []);

  /************************************************
   * 수상금액 포맷
   ************************************************/
  const formatMoney = (value?: string | number) => {
    if (!value) return "";
    const num = String(value).replace(/[^0-9]/g, "");
    if (!num) return "";
    return Number(num).toLocaleString("ko-KR");
  };

  /************************************************
   * 조회
   ************************************************/
  const buildSearchRequest = useCallback((src: typeof INITIAL_FILTERS) => {
    return {
      awardYear: src.awardYear === "all" ? "" : src.awardYear,
      empNm: (src.empNm ?? "").trim(),
      deptNm: src.deptNm === "all" ? "" : src.deptNm,
      postCd: src.postCd === "all" ? "" : src.postCd,
      awardCd: src.awardCd === "all" ? "" : src.awardCd,
    };
  }, []);

  
const EMPTY_SEARCH_REQUEST = {
  awardYear: "",
  empNm: "",
  deptNm: "",
  postCd: "",
  awardCd: "",
};

const loadAwardList = useCallback(
  (requestData: {
    awardYear: string;
    empNm: string;
    deptNm: string;
    postCd: string;
    awardCd: string;
  }) => {
    setLoading(true);

    commonProxy.callAsyncSvc(requestData, {
      txCd: "TREVLR0026",
      success: (response: any) => {
        const rtnData = response?.data?.data ?? response?.data ?? response ?? {};

        const list = rtnData?.AwardItems || rtnData?.awardItems || rtnData?.list || [];
        const yearList = rtnData?.awardYearList || [];
        const deptList = rtnData?.awardDeptList || [];

        const nextRows: IAwardRow[] = list.map((item: any, idx: number) => {
          const nextPostCd = String(item.postCd ?? "");

          return {
            id: Number(item.id ?? 0),
            key: String(item.id ?? idx),
            awardYear: item.awardYear ?? "",
            empNo: item.empNo ?? "",
            empNm: item.empNm ?? "",
            postCd: nextPostCd,
            postNm: posName(nextPostCd),
            deptNm: item.deptNm ?? "",
            awardCd: item.awardCd ?? null,
            awardVal: item.awardVal ?? null,
            lstChgEmpNo: item.lstChgEmpNo ?? "",
            lstChgDtm: item.lstChgDtm ?? "",
          };
        });

        const nextYearOptions: SelectOption[] = (yearList || [])
          .filter((y: any) => y !== null && y !== undefined && String(y).trim() !== "")
          .map((y: any) => ({
            value: String(y),
            label: `${y}년`,
          }));

        const nextDeptOptions: SelectOption[] = (deptList || [])
          .filter((item: any) => String(item.deptNm ?? "").trim() !== "")
          .map((item: any) => ({
            value: String(item.deptNm),
            label: String(item.deptNm),
          }));

        dispatch(Evl0112mAction.setRows(nextRows));
        setYearOptions(nextYearOptions);
        setDeptOptions(nextDeptOptions);
        setPageNo(1);
        setLoading(false);
      },
      fail: () => {
        setLoading(false);
        message.error("수상내역 조회 중 오류가 발생했습니다.");
      },
    });
  },
  [dispatch, posName]
);

  const initScreen = useCallback(() => {
    setFilters(INITIAL_FILTERS);
    dispatch(Evl0112mAction.setInquiryCnds(INITIAL_FILTERS));
    dispatch(Evl0112mAction.setRows([]));

    setSortKey("");
    setSortOrder("asc");
    setPageNo(1);

    loadAwardList(EMPTY_SEARCH_REQUEST);
  }, [dispatch, loadAwardList]);

  useEffect(() => {
    setPosClsList();
    loadAwardCodeList();
  }, [setPosClsList, loadAwardCodeList]);

  useEffect(() => {
    initScreen();
  }, [initScreen]);

  /************************************************
   * 이벤트
   ************************************************/
  const onSearch = useCallback(() => {
    const requestData = buildSearchRequest(filters);

    dispatch(Evl0112mAction.setInquiryCnds(filters));
    loadAwardList(requestData);
  }, [dispatch, filters, buildSearchRequest, loadAwardList]);

  const onReset = useCallback(() => {
    initScreen();
  }, [initScreen]);

  /************************************************
   * 추가 팝업
   ************************************************/
  const openAddPopup = useCallback(() => {
    const params: any = {
      title: "수상 내역 추가",
      width: "75%",
      height: 720,
      popupId: "evl0112p",

      accept: (popupData: any) => {
        const saveList = popupData?.list ?? [];

        if (!saveList.length) {
          message.warning("저장할 데이터가 없습니다.");
          return;
        }

        commonProxy.callAsyncSvc(
          { list: saveList },
          {
            txCd: "TREVLS0026",
            success: () => {
              message.success("수상 내역이 저장되었습니다.");
              initScreen();
            },
            fail: () => {
              message.error("수상 내역 저장 중 오류가 발생했습니다.");
            },
          }
        );
      },

      cancel: () => {},

      props: {},
    };

    common.openPopup(params);
  }, [loadAwardList]);

  /************************************************
   * 수정 / 삭제
   ************************************************/
  const openEditModal = useCallback(
    (row: IAwardRow) => {
      setSelectedRow(row);
      editForm.setFieldsValue({
        awardCd: row.awardCd ?? undefined,
        awardVal: formatMoney(row.awardVal ?? ""),
      });
      setEditOpen(true);
    },
    [editForm]
  );

  const saveEdit = useCallback(async () => {
    if (!selectedRow) return;

    const values = await editForm.validateFields();

    const requestData = {
      id: selectedRow.id,
      awardYear: selectedRow.awardYear,
      empNo: selectedRow.empNo,
      awardCd: values.awardCd,
      awardVal: values.awardVal
        ? String(values.awardVal).replace(/[^0-9]/g, "")
        : "",
    };

    commonProxy.callAsyncSvc(requestData, {
      txCd: "TREVLS0027",
      success: () => {
        message.success("수상 정보가 수정되었습니다.");
        setEditOpen(false);
        setSelectedRow(null);
        editForm.resetFields();
        initScreen();
      },
      fail: () => {
        message.error("수상 정보 수정 중 오류가 발생했습니다.");
      },
    });
  }, [selectedRow, editForm, loadAwardList]);

  const openDeleteModal = useCallback((row: IAwardRow) => {
    setSelectedRow(row);
    setDeleteOpen(true);
  }, []);

  const confirmDelete = useCallback(() => {
    if (!selectedRow) return;

    commonProxy.callAsyncSvc(
      { id: selectedRow.id },
      {
        txCd: "TREVLS0028",
        success: () => {
          message.success("수상 내역이 삭제되었습니다.");
          setDeleteOpen(false);
          setSelectedRow(null);
          initScreen();
        },
        fail: () => {
          message.error("수상 내역 삭제 중 오류가 발생했습니다.");
        },
      }
    );
  }, [selectedRow, loadAwardList]);

  /************************************************
   * 정렬 / 페이징
   ************************************************/
  const sortedRows = useMemo(() => {
    const arr = [...rows];

    if (!sortKey) return arr;

    arr.sort((a, b) => {
      let av: any = a[sortKey];
      let bv: any = b[sortKey];

      if (sortKey === "awardVal") {
        av = Number(av ?? 0);
        bv = Number(bv ?? 0);
      } else {
        av = String(av ?? "");
        bv = String(bv ?? "");
      }

      const result = av > bv ? 1 : av < bv ? -1 : 0;
      return sortOrder === "asc" ? result : -result;
    });

    return arr;
  }, [rows, sortKey, sortOrder]);

  const pagedRows = useMemo(() => {
    const start = (pageNo - 1) * pageSize;
    return sortedRows.slice(start, start + pageSize);
  }, [sortedRows, pageNo, pageSize]);

  const handleSort = useCallback(
    (key: SortKey) => {
      if (sortKey === key) {
        setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
        return;
      }
      setSortKey(key);
      setSortOrder("asc");
    },
    [sortKey]
  );

  /************************************************
   * 헤더 정렬 ui
   ************************************************/
  const renderSortTitle = useCallback(
    (
      label: string,
      key: SortKey,
      align: "left" | "center" | "right" = "center"
    ) => {
      const active = sortKey === key;

      return (
        <div
          onClick={() => handleSort(key)}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent:
              align === "right"
                ? "flex-end"
                : align === "left"
                ? "flex-start"
                : "center",
            gap: 4,
            cursor: "pointer",
            userSelect: "none",
          }}
        >
          <span>{label}</span>
          <span
            style={{
              display: "inline-flex",
              flexDirection: "column",
              lineHeight: 0.8,
            }}
          >
            <CaretUpOutlined
              style={{
                fontSize: 10,
                color: active && sortOrder === "asc" ? "#1677ff" : "#bfbfbf",
              }}
            />
            <CaretDownOutlined
              style={{
                fontSize: 10,
                color: active && sortOrder === "desc" ? "#1677ff" : "#bfbfbf",
                marginTop: 2,
              }}
            />
          </span>
        </div>
      );
    },
    [handleSort, sortKey, sortOrder]
  );

  const columns: ColumnsType<IAwardRow> = [
    {
      title: renderSortTitle("수상년도", "awardYear"),
      dataIndex: "awardYear",
      width: 100,
      align: "center",
    },
    {
      title: renderSortTitle("사원명", "empNm"),
      dataIndex: "empNm",
      width: 120,
      align: "center",
    },
    {
      title: renderSortTitle("직급", "postNm"),
      dataIndex: "postCd",
      width: 100,
      align: "center",
      render: (v: string) => posName(v) || "-",
    },
    {
      title: renderSortTitle("부서", "deptNm"),
      dataIndex: "deptNm",
      width: 140,
      align: "center",
      render: (v) => v || "-",
    },
    {
      title: renderSortTitle("수상명", "awardCd"),
      dataIndex: "awardCd",
      width: 140,
      align: "center",
      render: (v) => awardCdName(v),
    },
    {
      title: renderSortTitle("수상금액", "awardVal", "center"),
      dataIndex: "awardVal",
      width: 140,
      align: "right",
      render: (v) => (formatMoney(v) ? `${formatMoney(v)} 원` : "-"),
    },
    {
      title: "작업",
      key: "action",
      width: 140,
      align: "center",
      render: (_: any, row: IAwardRow) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEditModal(row)}>
            수정
          </Button>
          <Button
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => openDeleteModal(row)}
          >
            삭제
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ minHeight: "100%" }}>
      <div className="page-header">
        <Typography.Title
          level={2}
          className="page-title"
          style={{ textAlign: "center" }}
        >
          수상내역관리
        </Typography.Title>
      </div>

      <div style={{ maxWidth: "100%", margin: "0 auto" }}>
        <Row justify="end" style={{ marginBottom: 16 }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="large"
            onClick={openAddPopup}
          >
            추가
          </Button>
        </Row>

        <Row gutter={[12, 12]} align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Space size={4}>
              <span>수상년도 :</span>
              <Select
                value={filters.awardYear}
                style={{ width: 120 }}
                onChange={(value) => onChangeFilter({ awardYear: value })}
                options={[{ value: "all", label: "전체" }, ...yearOptions]}
              />
            </Space>
          </Col>

          <Col>
            <Space size={4}>
              <span>부서 :</span>
              <Select
                value={filters.deptNm}
                style={{ width: 140 }}
                onChange={(value) => onChangeFilter({ deptNm: value })}
                options={[{ value: "all", label: "전체" }, ...deptOptions]}
              />
            </Space>
          </Col>

          <Col>
            <Space size={4}>
              <span>직급 :</span>
              <Select
                value={filters.postCd}
                style={{ width: 140 }}
                onChange={(value) => onChangeFilter({ postCd: value })}
                options={[{ value: "all", label: "전체" }, ...posOptions]}
              />
            </Space>
          </Col>

          <Col>
            <Space size={4}>
              <span>사원명 :</span>
              <Input
                value={filters.empNm}
                onChange={(e) => onChangeFilter({ empNm: e.target.value })}
                placeholder="사원명 입력"
                prefix={<SearchOutlined />}
                style={{ width: 180 }}
                onPressEnter={onSearch}
              />
            </Space>
          </Col>

          <Col>
            <Space size={4}>
              <span>수상명 :</span>
              <Select
                value={filters.awardCd}
                style={{ width: 140 }}
                onChange={(value) => onChangeFilter({ awardCd: value })}
                options={[{ value: "all", label: "전체" }, ...awardCdOptions]}
              />
            </Space>
          </Col>

          <Col>
            <Space align="end">
              <Button type="primary" icon={<SearchOutlined />} onClick={onSearch}>
                검색
              </Button>

              <Button icon={<ReloadOutlined />} onClick={onReset}>
                초기화
              </Button>
            </Space>
          </Col>
        </Row>

        <div
          style={{
            background: "#fff",
            borderRadius: 10,
            boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
          }}
        >
          <Table
            rowKey="key"
            loading={loading}
            columns={columns}
            dataSource={pagedRows}
            pagination={{
              current: pageNo,
              pageSize,
              total: sortedRows.length,
              showSizeChanger: false,
              onChange: (page) => setPageNo(page),
              showTotal: (t, range) => `총 ${t}건 (${range[0]}-${range[1]})`,
            }}
            scroll={{ x: 1200 }}
            bordered
          />
        </div>
      </div>

      <Modal
        open={editOpen}
        title="수상 정보 수정"
        onOk={saveEdit}
        onCancel={() => {
          setEditOpen(false);
          setSelectedRow(null);
          editForm.resetFields();
        }}
        okText="확인"
        cancelText="취소"
        destroyOnClose
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            label="수상명"
            name="awardCd"
            rules={[{ required: true, message: "수상코드를 선택하세요." }]}
          >
            <Select options={awardCdOptions} />
          </Form.Item>
          <Form.Item label="수상금액" name="awardVal">
            <Input
              placeholder="금액 입력"
              style={{ textAlign: "right" }}
              onChange={(e) => {
                let onlyNumber = e.target.value.replace(/[^0-9]/g, "");

                if (Number(onlyNumber) > AWARD_MAX_VALUE) {
                  onlyNumber = String(AWARD_MAX_VALUE);
                }

                const formatted = formatMoney(onlyNumber);
                editForm.setFieldsValue({ awardVal: formatted });
              }}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={deleteOpen}
        title="수상 내역 삭제"
        onOk={confirmDelete}
        onCancel={() => {
          setDeleteOpen(false);
          setSelectedRow(null);
        }}
        okText="삭제"
        cancelText="취소"
        okButtonProps={{ danger: true }}
        destroyOnClose
      >
        {selectedRow && (
          <div style={{ lineHeight: 2 }}>
            <div>
              <b>사원명 :</b> {selectedRow.empNm}
            </div>
            <div>
              <b>직급 :</b> {posName(selectedRow.postCd) || "-"}
            </div>
            <div>
              <b>부서 :</b> {selectedRow.deptNm || "-"}
            </div>
            <div>
              <b>수상코드 :</b> {awardCdName(selectedRow.awardCd)}
            </div>
            <div>
              <b>수상금액 :</b> {formatMoney(selectedRow.awardVal)} 원
            </div>
            <div style={{ color: "#cf1322", marginTop: 12 }}>
              ※ 해당 직원의 수상 정보가 삭제됩니다.
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Evl0112m;